import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircleIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  TrophyIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import StudentSearchCombobox from '@/components/shared/StudentSearchCombobox';
import SubmissionEditor from '@/components/submissions/SubmissionEditor';
import SubmissionSummary from '@/components/submissions/SubmissionSummary';
import { TextArea } from '@/components/ui';
import { removeAttachments } from '@/services/attachments/attachmentUploadService';
import {
  CAMPAIGN_ACTION_TYPES,
  getCampaignActions,
  issueCampaignActionRecognition,
  isCampaignActionAvailable,
  submitCampaignResponse,
  subscribeCampaignResponses,
} from '../services/campaignService';

const typeLabels = {
  [CAMPAIGN_ACTION_TYPES.NOMINATE]: 'Learner nomination',
  [CAMPAIGN_ACTION_TYPES.RECOGNISE]: 'Learner recognition',
  [CAMPAIGN_ACTION_TYPES.ENTRY]: 'Entry',
  [CAMPAIGN_ACTION_TYPES.CONFIRM]: 'Confirmation',
};

const responseBadge = response => {
  if (!response) return { label: 'Not started', className: 'badge-ghost' };
  if (response.status === 'issuing') return { label: 'Issuing', className: 'badge-warning' };
  if (response.status === 'failed') return { label: 'Needs attention', className: 'badge-error' };
  return {
    label: response.actionType === CAMPAIGN_ACTION_TYPES.CONFIRM ? 'Complete' : 'Submitted',
    className: 'badge-success',
  };
};

function SelectedLearner({ student, onChange, disabled }) {
  if (!student) {
    return (
      <StudentSearchCombobox
        onSelect={onChange}
        placeholder="Search for a learner"
        autoFocus
      />
    );
  }

  const name = student.studentName || student.name || (
    `${student.firstName || ''} ${student.surname || student.lastName || ''}`.trim()
  );
  return (
    <div className="flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 p-3">
      <div>
        <div className="font-medium">{name}</div>
        {(student.className) && <div className="text-xs text-base-content/55">{student.className}</div>}
      </div>
      {!disabled && (
        <button type="button" className="btn btn-ghost btn-xs" onClick={() => onChange(null)}>
          Change
        </button>
      )}
    </div>
  );
}

function ActionCard({
  campaign,
  action,
  response,
  schoolId,
  user,
  currentRole,
  onCommitted,
  onAttachmentUploaded,
  onUploadBusyChange,
}) {
  const [editing, setEditing] = useState(!response);
  const [student, setStudent] = useState(response?.studentId ? response : null);
  const [submission, setSubmission] = useState({
    text: response?.text || '',
    attachments: response?.attachments || [],
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const uploadedAttachments = useRef([]);
  const available = isCampaignActionAvailable(campaign, action, currentRole);
  const canEdit = available && (!response || action.allowCorrection);
  const badge = responseBadge(response);

  useEffect(() => {
    if (!response || editing) return;
    setStudent(response.studentId ? response : null);
    setSubmission({ text: response.text || '', attachments: response.attachments || [] });
  }, [editing, response]);

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (action.type === CAMPAIGN_ACTION_TYPES.RECOGNISE) {
        await issueCampaignActionRecognition({
          schoolId,
          campaign,
          action,
          student,
          user,
          currentRole,
        });
        setSuccess(`${student?.firstName || student?.studentName || 'Learner'} recognised.`);
      } else {
        await submitCampaignResponse({
          schoolId,
          campaign,
          action,
          payload: {
            student,
            text: submission.text,
            attachments: submission.attachments,
          },
          user,
          currentRole,
        });
        onCommitted(submission.attachments || []);
        const retainedIds = new Set((submission.attachments || []).map(attachment => attachment.id));
        const abandonedUploads = uploadedAttachments.current.filter(
          attachment => !retainedIds.has(attachment.id)
        );
        const removedAttachments = (response?.attachments || []).filter(
          attachment => !retainedIds.has(attachment.id)
        );
        const cleanup = [...abandonedUploads, ...removedAttachments];
        uploadedAttachments.current = [];
        if (cleanup.length) {
          removeAttachments(cleanup).catch(cleanupError => (
            console.warn('[CampaignActionSheet] Replaced attachment cleanup failed:', cleanupError)
          ));
        }
        setSuccess(action.type === CAMPAIGN_ACTION_TYPES.CONFIRM
          ? 'Participation confirmed.'
          : 'Response submitted.');
      }
      setEditing(false);
    } catch (submitError) {
      setError(submitError.message || 'Could not complete this action');
    } finally {
      setSaving(false);
    }
  };

  const needsStudent = [
    CAMPAIGN_ACTION_TYPES.NOMINATE,
    CAMPAIGN_ACTION_TYPES.RECOGNISE,
  ].includes(action.type);
  const disabled = saving || !available;
  const missingRequiredText = (
    action.type === CAMPAIGN_ACTION_TYPES.ENTRY &&
    action.textRequired &&
    !submission.text.trim()
  );
  const missingEntry = (
    action.type === CAMPAIGN_ACTION_TYPES.ENTRY &&
    !submission.text.trim() &&
    !submission.attachments.length
  );
  const cancelCorrection = () => {
    const abandoned = uploadedAttachments.current;
    uploadedAttachments.current = [];
    if (abandoned.length) {
      removeAttachments(abandoned).catch(cleanupError => (
        console.warn('[CampaignActionSheet] Correction cleanup failed:', cleanupError)
      ));
    }
    setSubmission({ text: response?.text || '', attachments: response?.attachments || [] });
    setStudent(response?.studentId ? response : null);
    setEditing(false);
  };

  return (
    <article className="rounded-2xl border border-base-300 bg-base-100 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">
            {typeLabels[action.type]}
          </p>
          <h3 className="mt-1 text-lg font-semibold">{action.label}</h3>
          {action.instructions && (
            <p className="mt-1 text-sm text-base-content/65">{action.instructions}</p>
          )}
        </div>
        <span className={`badge badge-sm shrink-0 ${badge.className}`}>{badge.label}</span>
      </div>

      {!available && (
        <div className="alert mt-4 py-2 text-sm">
          <span>This action is closed.</span>
        </div>
      )}

      {response && !editing ? (
        <div className="mt-4 rounded-xl bg-base-200/60 p-4">
          {needsStudent && (
            <div>
              <div className="font-medium">{response.studentName}</div>
              {response.className && <div className="text-xs text-base-content/55">{response.className}</div>}
            </div>
          )}
          {action.type === CAMPAIGN_ACTION_TYPES.ENTRY && (
            <SubmissionSummary text={response.text} attachments={response.attachments} />
          )}
          {action.type === CAMPAIGN_ACTION_TYPES.CONFIRM && (
            <div className="flex items-start gap-2">
              <CheckCircleIcon className="mt-0.5 h-5 w-5 text-success" />
              <div>
                <div className="font-medium">Confirmed</div>
                {response.text && <p className="mt-1 text-sm text-base-content/65">{response.text}</p>}
              </div>
            </div>
          )}
          {canEdit && (
            <button type="button" className="btn btn-ghost btn-sm mt-3" onClick={() => setEditing(true)}>
              Correct response
            </button>
          )}
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          {needsStudent && (
            <div>
              <label className="mb-2 block text-sm font-medium">Learner</label>
              <SelectedLearner student={student} onChange={setStudent} disabled={disabled} />
            </div>
          )}

          {action.type === CAMPAIGN_ACTION_TYPES.NOMINATE && (
            <TextArea
              label="Nomination reason"
              instruction="Optional"
              value={submission.text}
              onChange={event => setSubmission(current => ({ ...current, text: event.target.value }))}
              maxLength={500}
              rows={3}
              disabled={disabled}
              placeholder="Add a short reason for the reviewer"
            />
          )}

          {action.type === CAMPAIGN_ACTION_TYPES.ENTRY && (
            <SubmissionEditor
              value={submission}
              onChange={setSubmission}
              schoolId={schoolId}
              domain={`campaigns/${user.uid}`}
              currentUser={user}
              textLabel="Your response"
              textInstruction={action.textRequired ? 'A written response is required.' : 'Add text, attachments, or both.'}
              textRequired={action.textRequired}
              allowAttachments={action.allowAttachments}
              maxFiles={action.maxFiles || 5}
              disabled={disabled}
              deleteOnRemove={!response}
              onAttachmentUploaded={attachment => {
                uploadedAttachments.current.push(attachment);
                onAttachmentUploaded(attachment);
              }}
              onUploadBusyChange={onUploadBusyChange}
            />
          )}

          {action.type === CAMPAIGN_ACTION_TYPES.CONFIRM && (
            <TextArea
              label="Note"
              instruction="Optional"
              value={submission.text}
              onChange={event => setSubmission(current => ({ ...current, text: event.target.value }))}
              maxLength={500}
              rows={3}
              disabled={disabled}
              placeholder="Add anything the campaign owner should know"
            />
          )}

          {action.type === CAMPAIGN_ACTION_TYPES.RECOGNISE && action.recognition?.valueName && (
            <div className="flex items-start gap-2 rounded-xl bg-success/10 p-3 text-sm">
              <SparklesIcon className="mt-0.5 h-4 w-4 text-success" />
              <span>
                Issues the normal <strong>{action.recognition.valueName}</strong> recognition.
              </span>
            </div>
          )}

          {error && <div className="alert alert-error text-sm" role="alert"><span>{error}</span></div>}
          {success && <div className="alert alert-success text-sm" role="status"><span>{success}</span></div>}

          <div className="flex flex-wrap justify-end gap-2">
            {response && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={cancelCorrection}>
                Cancel
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={submit}
              disabled={
                disabled ||
                (needsStudent && !student) ||
                missingRequiredText ||
                missingEntry
              }
            >
              {saving
                ? <span className="loading loading-spinner loading-xs" />
                : action.type === CAMPAIGN_ACTION_TYPES.CONFIRM
                  ? <CheckCircleIcon className="h-4 w-4" />
                  : action.type === CAMPAIGN_ACTION_TYPES.RECOGNISE
                    ? <TrophyIcon className="h-4 w-4" />
                    : <PaperAirplaneIcon className="h-4 w-4" />}
              {response ? 'Save correction' : (
                action.type === CAMPAIGN_ACTION_TYPES.CONFIRM
                  ? 'Confirm'
                  : action.type === CAMPAIGN_ACTION_TYPES.RECOGNISE
                    ? 'Issue recognition'
                    : 'Submit'
              )}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

export default function CampaignActionSheet({
  campaign,
  schoolId,
  user,
  currentRole,
  onClose,
}) {
  const [responses, setResponses] = useState([]);
  const [error, setError] = useState('');
  const committedAttachmentIds = useRef(new Set());
  const draftAttachments = useRef(new Map());
  const uploadBusy = useRef(new Map());
  const [closeError, setCloseError] = useState('');
  const actions = useMemo(() => (
    getCampaignActions(campaign).filter(action => (
      !action.audienceRoles?.length || action.audienceRoles.includes(currentRole)
    ))
  ), [campaign, currentRole]);

  useEffect(() => (
    subscribeCampaignResponses(
      schoolId,
      campaign.id,
      setResponses,
      subscriptionError => setError(subscriptionError.message || 'Could not load responses')
    )
  ), [campaign.id, schoolId]);

  const ownResponses = useMemo(() => Object.fromEntries(
    responses
      .filter(response => response.submittedById === user.uid)
      .map(response => [response.actionId, response])
  ), [responses, user.uid]);

  const handleCommitted = attachments => {
    attachments.forEach(attachment => committedAttachmentIds.current.add(attachment.id));
  };

  const close = async () => {
    if ([...uploadBusy.current.values()].some(Boolean)) {
      setCloseError('Wait for uploads to finish before closing this campaign.');
      return;
    }
    const savedIds = new Set([
      ...committedAttachmentIds.current,
      ...responses.flatMap(response => response.attachments || []).map(attachment => attachment.id),
    ]);
    const abandoned = [...draftAttachments.current.values()]
      .flat()
      .filter(attachment => !savedIds.has(attachment.id));
    if (abandoned.length) {
      removeAttachments(abandoned).catch(cleanupError => (
        console.warn('[CampaignActionSheet] Draft cleanup failed:', cleanupError)
      ));
    }
    onClose();
  };

  return (
    <div className="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="campaign-actions-title">
      <div className="modal-box max-h-[92vh] max-w-3xl p-0">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-base-300 bg-base-100 p-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">Campaign actions</p>
            <h2 id="campaign-actions-title" className="mt-1 text-2xl font-bold">{campaign.title}</h2>
            <p className="mt-1 text-sm text-base-content/60">
              Complete the actions that apply to you. Your progress is saved here.
            </p>
          </div>
          <button type="button" className="btn btn-ghost btn-sm btn-circle" onClick={close} aria-label="Close campaign actions">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <div className="space-y-4 p-4 sm:p-5">
          {closeError && <div className="alert alert-warning text-sm"><span>{closeError}</span></div>}
          {error && <div className="alert alert-error text-sm"><span>{error}</span></div>}
          {!actions.length ? (
            <div className="rounded-xl border border-dashed border-base-300 p-8 text-center text-base-content/55">
              There are no actions for your role.
            </div>
          ) : actions.map(action => (
            <div key={action.id}>
              <ActionCard
                campaign={campaign}
                action={action}
                response={ownResponses[action.id]}
                schoolId={schoolId}
                user={user}
                currentRole={currentRole}
                onCommitted={handleCommitted}
                onAttachmentUploaded={attachment => {
                  const attachments = draftAttachments.current.get(action.id) || [];
                  if (!attachments.some(item => item.id === attachment.id)) {
                    draftAttachments.current.set(action.id, [...attachments, attachment]);
                  }
                }}
                onUploadBusyChange={busy => uploadBusy.current.set(action.id, busy)}
              />
            </div>
          ))}
        </div>
      </div>
      <button className="modal-backdrop" onClick={close} aria-label="Close campaign actions">close</button>
    </div>
  );
}
